
// All MarianMT inference runs inside a Web Worker so the main thread never blocks.
// A singleton worker is shared across all translation calls.

export interface SupportedLanguage {
  code: string;
  label: string;
  nativeName: string;
}

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  { code: 'en', label: 'English',    nativeName: 'English'    },
  { code: 'hi', label: 'Hindi',      nativeName: 'हिन्दी'       },
  { code: 'es', label: 'Spanish',    nativeName: 'Español'    },
  { code: 'fr', label: 'French',     nativeName: 'Français'   },
  { code: 'de', label: 'German',     nativeName: 'Deutsch'    },
  { code: 'it', label: 'Italian',    nativeName: 'Italiano'   },
  { code: 'pt', label: 'Portuguese', nativeName: 'Português'  },
  { code: 'ru', label: 'Russian',    nativeName: 'Русский'    },
  { code: 'zh', label: 'Chinese',    nativeName: '中文'        },
  { code: 'ja', label: 'Japanese',   nativeName: '日本語'       },
  { code: 'ar', label: 'Arabic',     nativeName: 'العربية'    },
  { code: 'ko', label: 'Korean',     nativeName: '한국어'       },
];

// Map target language code → Xenova ONNX model id
// Xenova/* are pre-converted ONNX versions of Helsinki-NLP MarianMT models
const MODEL_FOR_TARGET: Record<string, string> = {
  en: 'Xenova/opus-mt-mul-en',
  hi: 'Xenova/opus-mt-en-hi',
  es: 'Xenova/opus-mt-en-es',
  fr: 'Xenova/opus-mt-en-fr',
  de: 'Xenova/opus-mt-en-de',
  it: 'Xenova/opus-mt-en-it',
  pt: 'Xenova/opus-mt-en-pt',
  ru: 'Xenova/opus-mt-en-ru',
  zh: 'Xenova/opus-mt-en-zh',
  ja: 'Xenova/opus-mt-en-jap',
  ar: 'Xenova/opus-mt-en-ar',
  ko: 'Xenova/opus-mt-en-ko',
};

const MUL_EN_MODEL = 'Xenova/opus-mt-mul-en';

// ── Singleton worker ──────────────────────────────────────────────────────────

let worker: Worker | null = null;

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(
      new URL('../workers/translation.worker.ts', import.meta.url),
      { type: 'module' },
    );
  }
  return worker;
}

// ── Core worker call ──────────────────────────────────────────────────────────

function runTranslation(
  text: string,
  modelId: string,
  onModelProgress?: (pct: number) => void,
  onProgress?: (pct: number) => void,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const w = getWorker();

    const handler = (event: MessageEvent) => {
      const msg = event.data as { type: string; pct?: number; translation?: string; message?: string };
      switch (msg.type) {
        case 'model-progress':
          onModelProgress?.(msg.pct ?? 0);
          break;
        case 'progress':
          onProgress?.(msg.pct ?? 0);
          break;
        case 'done':
          w.removeEventListener('message', handler);
          resolve(msg.translation ?? '');
          break;
        case 'error':
          w.removeEventListener('message', handler);
          reject(new Error(msg.message ?? 'Translation failed'));
          break;
      }
    };

    w.addEventListener('message', handler);
    w.postMessage({ type: 'translate', text, modelId });
  });
}

// ── Public API ────────────────────────────────────────────────────────────────

export const translateText = async (
  text: string,
  sourceLang: string,   // language code or 'auto'
  targetLang: string,   // language code
  onModelProgress?: (pct: number) => void,
  onProgress?: (pct: number) => void,
): Promise<string> => {
  const isSourceEnglish = sourceLang === 'en' || sourceLang === 'auto';

  if (targetLang === 'en') {
    // Any → English
    return runTranslation(text, MUL_EN_MODEL, onModelProgress, onProgress);
  }

  if (isSourceEnglish) {
    // English → target
    const model = MODEL_FOR_TARGET[targetLang];
    if (!model) throw new Error(`Unsupported target language: ${targetLang}`);
    return runTranslation(text, model, onModelProgress, onProgress);
  }

  // Non-English → Non-English: chain via English
  // Phase 1: source → English (uses mul-en model)
  // Phase 2: English → target
  // We split progress callbacks: 0–50% for phase 1, 50–100% for phase 2.
  const phase1ModelProgress = (pct: number) => onModelProgress?.(pct);
  const phase1Progress      = (pct: number) => onProgress?.(Math.round(pct * 0.5));

  const intermediate = await runTranslation(text, MUL_EN_MODEL, phase1ModelProgress, phase1Progress);

  const phase2ModelProgress = (pct: number) => onModelProgress?.(pct);
  const phase2Progress      = (pct: number) => onProgress?.(50 + Math.round(pct * 0.5));

  const model = MODEL_FOR_TARGET[targetLang];
  if (!model) throw new Error(`Unsupported target language: ${targetLang}`);

  return runTranslation(intermediate, model, phase2ModelProgress, phase2Progress);
};
